import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Client, Environment } from '@zappar/zw-api-client';
import { EchoClient, type Comment, type CommentCreatedEvent } from './client';
import type { SystemMessage } from './systemMessages';
import type { IAsset } from '../providers';

const toEchoEnv = (env: Environment): 'DEVELOPMENT' | 'STAGING' | 'PRODUCTION' => {
  if (env === Environment.Staging) return 'STAGING';
  if (env === Environment.Prod) return 'PRODUCTION';
  return 'DEVELOPMENT';
};

function assetToResourceId(asset: IAsset): string {
  return `zml:${asset.getId()}`;
}

export interface EchoContextValue {
  isConnected: boolean;
  error: string | null;
  /**
   * Post a system comment for any asset by its ID. Pass the result of a body
   * builder from `systemMessages.ts` — the event key on that object is sent on
   * the wire as `kind: "system:${eventKey}"`.
   * Safe to call while still connecting — messages are queued and flushed once the
   * WebSocket is open.
   */
  postSystemComment: (assetId: string, message: SystemMessage) => void;
  /** Comments for the currently subscribed asset. */
  comments: Comment[];
  /** True while the initial page of comments is being fetched. */
  isLoadingComments: boolean;
  /** The resourceId (`zml:{assetId}`) that CommentsTab is currently subscribed to. */
  subscribedAssetId: string | null;
  /** Subscribe to real-time comment updates for an asset. Clears previous subscription. */
  subscribeToAsset: (asset: IAsset) => void;
  /** Unsubscribe from real-time comment updates and clear comment state. */
  unsubscribeFromAsset: (asset: IAsset) => void;
  /**
   * Post a user comment to the currently subscribed asset. The returned promise resolves
   * when the server echoes back a matching `commentCreated` event (matched on resourceId
   * and body — the wire protocol has no correlation ID). Rejects on a 10-second timeout.
   */
  postComment: (body: string) => Promise<void>;
  /** Delete a comment from the currently subscribed asset. Optimistically removes it from state. */
  deleteComment: (commentId: string) => void;
}

const EchoContext = createContext<EchoContextValue>({
  isConnected: false,
  error: null,
  postSystemComment: () => {},
  comments: [],
  isLoadingComments: false,
  subscribedAssetId: null,
  subscribeToAsset: () => {},
  unsubscribeFromAsset: () => {},
  postComment: () => Promise.resolve(),
  deleteComment: () => {},
});

export const useEchoContext = (): EchoContextValue => useContext(EchoContext);

interface EchoProviderProps {
  zwApiClient: Client;
  workspaceId: string;
  children: React.ReactNode;
}

export const EchoProvider = ({ zwApiClient, workspaceId, children }: EchoProviderProps) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [subscribedAssetId, setSubscribedAssetId] = useState<string | null>(null);

  const echoRef = useRef<EchoClient | null>(null);
  /** Keeps the current subscribed resourceId accessible inside event handlers without stale closures. */
  const subscribedResourceIdRef = useRef<string | null>(null);
  /** System messages queued before the WebSocket connection is open. */
  const pendingSystemMessagesRef = useRef<Array<{ resourceId: string; message: SystemMessage }>>([]);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;

    const resolveToken = async (): Promise<string | null> => {
      const existing = zwApiClient.getAccessToken();
      if (existing) return existing;
      if (zwApiClient.useCookieAuth) {
        const resp = await zwApiClient.apiRequest<{ token: string }>('/echo-token/', {}, 'POST');
        return resp.token ?? null;
      }
      return null;
    };

    const setup = async () => {
      let accessToken: string | null;
      try {
        accessToken = await resolveToken();
      } catch {
        if (!cancelled) setError('Failed to obtain a comments session token.');
        return;
      }
      if (cancelled) return;

      if (!accessToken) {
        setError('Comments are unavailable for this session (missing access token).');
        return;
      }

      const cmsEnv = toEchoEnv(zwApiClient.env);
      const useDev = zwApiClient.env === Environment.Local || zwApiClient.env === Environment.Dev;

      const client = new EchoClient({
        accessToken,
        cmsEnv,
        // Workspace-scoped connection: authorises the client to subscribe to and
        // post comments on any asset within this workspace.
        resourceId: `workspace:${workspaceId}`,
        workspaceId,
        useDev,
      });
      echoRef.current = client;

      const mergeComments = (incoming: Comment[], forResourceId: string) => {
        if (forResourceId !== subscribedResourceIdRef.current) return;
        setComments(prev => {
          const merged = new Map(prev.map(c => [c.commentId, c]));
          incoming.forEach(c => merged.set(c.commentId, c));
          // Newest first — CommentsTab renders the list top-down with the most recent comment at the top.
          return Array.from(merged.values()).sort((a, b) => b.createdAt - a.createdAt);
        });
      };

      client
        .on('open', () => {
          if (cancelled) return;
          setIsConnected(true);
          setError(null);

          // If CommentsTab already called subscribeToAsset before the socket opened, catch up now.
          const currentResourceId = subscribedResourceIdRef.current;
          if (currentResourceId) {
            client.subscribe(currentResourceId);
            client.listComments({ resourceId: currentResourceId, limit: 100 });
          }

          // Flush system messages that arrived before the connection was ready.
          const pending = pendingSystemMessagesRef.current.splice(0);
          for (const { resourceId, message } of pending) {
            client.postComment(resourceId, message.body, { kind: `system:${message.eventKey}` });
          }
        })
        .on('close', () => {
          if (!cancelled) setIsConnected(false);
        })
        .on('error', () => {
          if (!cancelled) setError('Failed to connect to comments service.');
        })
        .on('listCommentsResult', (data) => {
          if (cancelled) return;
          mergeComments(data.items ?? [], data.resourceId);
          setIsLoadingComments(false);
        })
        .on('commentCreated', (data) => {
          if (cancelled) return;
          mergeComments([data.comment], data.resourceId);
        })
        .on('commentsDeleted', (data) => {
          if (cancelled) return;
          if (data.resourceId !== subscribedResourceIdRef.current) return;
          const deletedIds = new Set(data.commentIds);
          setComments(prev => prev.filter(c => !deletedIds.has(c.commentId)));
        });

      client.connect();
    };

    setup();

    return () => {
      cancelled = true;
      const client = echoRef.current;
      if (client) {
        const currentResourceId = subscribedResourceIdRef.current;
        if (currentResourceId) {
          try { client.unsubscribe(currentResourceId); } catch { /* ignore cleanup errors */ }
        }
        client.disconnect();
        echoRef.current = null;
      }
    };
  }, [zwApiClient, workspaceId]);

  const postSystemComment = useCallback((assetId: string, message: SystemMessage) => {
    const resourceId = `zml:${assetId}`;
    const client = echoRef.current;
    if (client?.connected) {
      client.postComment(resourceId, message.body, { kind: `system:${message.eventKey}` });
    } else {
      // Queue for when the connection opens.
      pendingSystemMessagesRef.current.push({ resourceId, message });
    }
  }, []);

  const subscribeToAsset = useCallback((asset: IAsset) => {
    const resourceId = assetToResourceId(asset);
    const client = echoRef.current;

    // Unsubscribe from the previous asset if switching.
    const prevResourceId = subscribedResourceIdRef.current;
    if (prevResourceId && prevResourceId !== resourceId && client?.connected) {
      try { client.unsubscribe(prevResourceId); } catch { /* ignore */ }
    }

    subscribedResourceIdRef.current = resourceId;
    setSubscribedAssetId(resourceId);
    setComments([]);
    setIsLoadingComments(true);

    if (client?.connected) {
      client.subscribe(resourceId);
      client.listComments({ resourceId, limit: 100 });
    }
    // If not yet connected, the `open` handler will subscribe automatically.
  }, []);

  const unsubscribeFromAsset = useCallback((asset: IAsset) => {
    const resourceId = assetToResourceId(asset);
    const client = echoRef.current;
    if (client?.connected) {
      try { client.unsubscribe(resourceId); } catch { /* ignore */ }
    }
    subscribedResourceIdRef.current = null;
    setSubscribedAssetId(null);
    setComments([]);
    setIsLoadingComments(false);
  }, []);

  const postComment = useCallback((body: string): Promise<void> => {
    const resourceId = subscribedResourceIdRef.current;
    const client = echoRef.current;
    if (!client?.connected || !resourceId) {
      return Promise.reject(new Error('EchoClient: not connected or no asset subscribed'));
    }
    return new Promise<void>((resolve, reject) => {
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      const cleanup = () => {
        if (timeoutId !== null) clearTimeout(timeoutId);
        client.off('commentCreated', onCreated);
      };
      const onCreated = (data: CommentCreatedEvent) => {
        if (data.resourceId !== resourceId) return;
        if (data.comment.body !== body) return;
        cleanup();
        resolve();
      };
      timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error('Timed out waiting for comment confirmation'));
      }, 10_000);
      client.on('commentCreated', onCreated);
      try {
        client.postComment(resourceId, body);
      } catch (err) {
        cleanup();
        reject(err);
      }
    });
  }, []);

  const deleteComment = useCallback((commentId: string) => {
    const resourceId = subscribedResourceIdRef.current;
    const client = echoRef.current;
    if (!client?.connected || !resourceId) throw new Error('EchoClient: not connected or no asset subscribed');
    client.deleteComment(resourceId, commentId);
    // Optimistically remove from state; the server also sends a `commentsDeleted` event.
    setComments(prev => prev.filter(c => c.commentId !== commentId));
  }, []);

  return (
    <EchoContext.Provider value={{
      isConnected,
      error,
      postSystemComment,
      comments,
      isLoadingComments,
      subscribedAssetId,
      subscribeToAsset,
      unsubscribeFromAsset,
      postComment,
      deleteComment,
    }}>
      {children}
    </EchoContext.Provider>
  );
};
